import { useCallback, useState } from "react";
import themeGet from "@styled-system/theme-get";
import { Flex, H5, Box, Divider, Small } from "orcs-design-system";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import useAIDashboards from "src/hooks/useAIDashboards";
import AIIcon from "src/components/AIContent/AI_Icon";
import ErrorNotification from "src/components/ErrorNotification";
import Url, { url } from "src/consts/urlPaths";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import DashboardListItem from "./DashboardListItem";
import DeleteDashboardModal from "./DeleteDashboardModal";
import useDashboardRename from "./useDashboardRename";
import useDashboardDeletion from "./useDashboardDeletion";

const LibraryNavLink = styled(NavLink)`
  display: block;
  text-align: center;
  text-decoration: none !important;
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.2")};
  color: ${themeGet("colors.bodyText")};
  background-color: ${themeGet("colors.tableRowHeader")};
  border-radius: ${themeGet("radii.2")};
  padding: ${themeGet("space.s")};
  transition: ${themeGet("transition.transitionDefault")};

  &.active {
    background-color: ${themeGet("colors.navActiveBackground")};
  }

  &:hover,
  &:focus {
    color: ${themeGet("colors.bodyText")};
    background-color: ${themeGet("colors.hoverSurface")};
  }
`;

const AIDashboardsContent = () => {
  const {
    dashboards,
    loading,
    error: loadError,
    renameDashboard,
    hideDashboard,
  } = useAIDashboards();
  const { enableAIDataDrivenDashboards } = useWorkspaceFeatureFlags();

  // Shared by both flows below, last failure wins - matches the single
  // error state this component had before rename/delete were split out.
  const [error, setError] = useState<Error | null>(null);
  const clearError = useCallback(() => setError(null), []);

  const {
    editingId,
    draftTitle,
    setDraftTitle,
    startRename,
    saveRename,
    cancelRename,
  } = useDashboardRename(renameDashboard, setError);

  const {
    dashboardToDelete,
    requestDelete,
    isDeleting,
    confirmDelete,
    closeDeleteModal,
  } = useDashboardDeletion(hideDashboard, setError);

  if (loading) {
    return (
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100%"
        p="r"
      >
        <Small color="greyMedium">Loading dashboards...</Small>
      </Flex>
    );
  }

  return (
    // Block-level, not a flex container: the panel's close button is floated
    // right, and a flex container refuses to overlap a float — which would
    // narrow every row by the width of that button.
    <>
      <Box mb="r">
        <Flex alignItems="center" gap="xs">
          <AIIcon width={20} height={20} />

          <H5 fontWeight="bold" data-testid="heading">
            Dashboards
          </H5>
        </Flex>
        <Box mt="r">
          <LibraryNavLink
            to={url(Url.AI_DASHBOARDS_LIBRARY)}
            data-testid="dashboard-library-link"
          >
            Dashboard Library
          </LibraryNavLink>
        </Box>

        <Box my="r">
          <Divider light />
        </Box>
        {!dashboards || dashboards.length === 0 ? (
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            p="r"
          >
            <Small
              color="greyMedium"
              textAlign="center"
              data-testid="ai-dashboards-empty"
            >
              {loadError
                ? "Couldn't load your dashboards. Try again in a moment."
                : "No AI-generated dashboards yet. Open the library or ask the AI to create one in a chat."}
            </Small>
          </Flex>
        ) : (
          <Flex flexDirection="column" gap="xs">
            {dashboards.map((dashboard) => (
              <DashboardListItem
                key={dashboard.id}
                dashboard={dashboard}
                enableAIDataDrivenDashboards={enableAIDataDrivenDashboards}
                isEditing={editingId === dashboard.id}
                draftTitle={draftTitle}
                onDraftTitleChange={setDraftTitle}
                onStartRename={startRename}
                onSaveRename={saveRename}
                onCancelRename={cancelRename}
                onDelete={requestDelete}
              />
            ))}
          </Flex>
        )}
      </Box>

      {dashboardToDelete && (
        <DeleteDashboardModal
          dashboard={dashboardToDelete}
          isDeleting={isDeleting}
          onConfirm={confirmDelete}
          onClose={closeDeleteModal}
        />
      )}

      {error && (
        <ErrorNotification
          message={error.message}
          error={error}
          onDismiss={clearError}
        />
      )}
    </>
  );
};

export default AIDashboardsContent;
